import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  MessageCircle,
  Send,
  Paperclip,
  Download,
  Trash2,
  Edit,
  X,
  File,
  CalendarPlus,
} from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { isUnauthorizedError } from '@/lib/authUtils';
import type {
  TaskCommentWithUser,
  TaskWithRelations,
  User,
} from '@shared/schema';

interface TaskCommentsProps {
  taskId: string;
  currentUser?: User;
  requestExtension?: boolean; // If true, show extension request form
  task?: TaskWithRelations; // Task data for admin to extend due date
  onDueDateExtended?: () => void; // Called when due date is extended (e.g. close modal and refresh board)
  onCommentsViewed?: () => void; // Called when user opens and views comments - clears "new comment" alert on task card
}

export default function TaskComments({
  taskId,
  currentUser,
  requestExtension = false,
  task,
  onDueDateExtended,
  onCommentsViewed,
}: TaskCommentsProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState('');
  const [attachments, setAttachments] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const [extensionDays, setExtensionDays] = useState<number>(1);
  const [isExtensionMode, setIsExtensionMode] = useState(requestExtension);
  const [isExtendDueDateOpen, setIsExtendDueDateOpen] = useState(false);
  const [newDueDate, setNewDueDate] = useState('');
  const isAdmin = currentUser?.role === 'admin';
  // Task creator (GM/PM/HOD) can extend due date when assignee requests extension (like admin portal)
  const isTaskCreator =
    !!task &&
    !!task.createdBy &&
    task.createdBy === currentUser?.id &&
    ['gm', 'pm', 'hod', 'dpm'].includes(currentUser?.role || '');
  const canExtendDueDate = isAdmin || isTaskCreator;

  // Fetch comments for this task
  const { data: comments = [], isLoading } = useQuery<TaskCommentWithUser[]>({
    queryKey: [`/api/tasks/${taskId}/comments`],
    enabled: !!taskId,
  });

  const scrollToBottom = () => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [comments]);

  // Mark comments as viewed when user opens this section - clears alert dot on task card
  useEffect(() => {
    onCommentsViewed?.();
  }, [taskId, onCommentsViewed]);

  // Sync extension mode with prop - but only for non-admins
  useEffect(() => {
    if (!isAdmin) {
      setIsExtensionMode(requestExtension);
    } else {
      setIsExtensionMode(false); // Never show extension request form for admins
    }
  }, [requestExtension, isAdmin]);

  const createCommentMutation = useMutation({
    mutationFn: async ({
      content,
      attachments,
    }: {
      content: string;
      attachments: string[];
    }) => {
      await apiRequest('POST', `/api/tasks/${taskId}/comments`, {
        content,
        attachments,
      });
    },
    onSuccess: () => {
      setNewComment('');
      setAttachments([]);
      queryClient.invalidateQueries({
        queryKey: [`/api/tasks/${taskId}/comments`],
      });
      toast({
        title: 'Comment Added',
        description: 'Your comment has been posted successfully',
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to post comment',
        variant: 'destructive',
      });
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: async ({
      commentId,
      content,
    }: {
      commentId: string;
      content: string;
    }) => {
      await apiRequest('PUT', `/api/comments/${commentId}`, { content });
    },
    onSuccess: () => {
      setEditingComment(null);
      setEditContent('');
      queryClient.invalidateQueries({
        queryKey: [`/api/tasks/${taskId}/comments`],
      });
      toast({
        title: 'Comment Updated',
        description: 'Your comment has been updated successfully',
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to update comment',
        variant: 'destructive',
      });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      await apiRequest('DELETE', `/api/comments/${commentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/tasks/${taskId}/comments`],
      });
      toast({
        title: 'Comment Deleted',
        description: 'Comment has been removed successfully',
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to delete comment',
        variant: 'destructive',
      });
    },
  });

  const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

  const uploadFile = async (file: File): Promise<string> => {
    if (file.size > MAX_BYTES) {
      throw new Error('File exceeds 5 MB limit');
    }
    try {
      // Request presigned PUT
      const uploadResponse = (await apiRequest('POST', '/api/objects/upload', {
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
      })) as any;
      const { uploadURL, objectUrl } = uploadResponse;

      // Upload file to storage
      const response = await fetch(uploadURL, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      // Return the canonical object URL we save to DB
      return objectUrl;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files) return;

    setUploading(true);
    try {
      const uploadPromises = Array.from(files).map(uploadFile);
      const fileUrls = await Promise.all(uploadPromises);
      setAttachments(prev => [...prev, ...fileUrls]);
      toast({
        title: 'Files Uploaded',
        description: `${files.length} file(s) uploaded successfully`,
      });
    } catch (error) {
      toast({
        title: 'Upload Failed',
        description: 'Failed to upload one or more files',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const createExtensionRequestMutation = useMutation({
    mutationFn: async ({
      content,
      days,
    }: {
      content: string;
      days: number;
    }) => {
      const response = await apiRequest('POST', `/api/tasks/${taskId}/extension-request`, {
        reason: content.trim(),
        requestedDays: days,
      });
      // Also create a comment with the extension request
      await apiRequest('POST', `/api/tasks/${taskId}/comments`, {
        content: `Extension Request: ${days} day(s). Reason: ${content.trim()}`,
        attachments: [],
      });
      return response;
    },
    onSuccess: () => {
      setNewComment('');
      setExtensionDays(1);
      setIsExtensionMode(false);
      queryClient.invalidateQueries({
        queryKey: [`/api/tasks/${taskId}/comments`],
      });
      queryClient.invalidateQueries({
        queryKey: ['/api/tasks'],
      });
      toast({
        title: 'Extension Request Submitted',
        description:
          'Your extension request has been sent to the task creator. They can extend the due date from this task.',
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to submit extension request',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = () => {
    if (isExtensionMode) {
      if (!newComment.trim()) {
        toast({
          title: 'Error',
          description: 'Please provide a reason for the extension request',
          variant: 'destructive',
        });
        return;
      }
      if (extensionDays < 1) {
        toast({
          title: 'Error',
          description: 'Please request at least 1 day',
          variant: 'destructive',
        });
        return;
      }
      createExtensionRequestMutation.mutate({
        content: newComment.trim(),
        days: extensionDays,
      });
      return;
    }

    if (!newComment.trim() && attachments.length === 0) return;

    createCommentMutation.mutate({
      content: newComment.trim(),
      attachments,
    });
  };

  const extendDueDateMutation = useMutation({
    mutationFn: async ({ dueDate }: { dueDate: string }) => {
      await apiRequest('PATCH', `/api/tasks/${taskId}`, {
        dueDate: new Date(dueDate).toISOString(),
      });
    },
    onSuccess: () => {
      setIsExtendDueDateOpen(false);
      setNewDueDate('');
      // Invalidate so board and lists show updated task in real time (no refresh)
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      queryClient.invalidateQueries({
        queryKey: [`/api/tasks/${taskId}/comments`],
      });
      const userTasksPredicate = (query: { queryKey: unknown[] }) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === '/api/users' &&
        query.queryKey[2] === 'tasks';
      queryClient.invalidateQueries({ predicate: userTasksPredicate });
      queryClient.refetchQueries({ predicate: userTasksPredicate });
      queryClient.invalidateQueries({
        predicate: (query: { queryKey: unknown[] }) =>
          Array.isArray(query.queryKey) &&
          typeof query.queryKey[0] === 'string' &&
          query.queryKey[0].startsWith('/api/users/') &&
          query.queryKey[0].endsWith('/tasks'),
      });
      queryClient.invalidateQueries({ queryKey: ['/api/analytics/task-stats'] });
      toast({
        title: 'Due Date Extended',
        description: 'Task due date has been successfully extended',
      });
      onDueDateExtended?.();
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to extend due date',
        variant: 'destructive',
      });
    },
  });

  const handleExtendDueDate = () => {
    if (!newDueDate) {
      toast({
        title: 'Error',
        description: 'Please select a new due date',
        variant: 'destructive',
      });
      return;
    }
    extendDueDateMutation.mutate({ dueDate: newDueDate });
  };

  const handleEdit = (comment: TaskCommentWithUser) => {
    setEditingComment(comment.id);
    setEditContent(comment.content);
  };

  const handleUpdate = (commentId: string) => {
    if (!editContent.trim()) return;

    updateCommentMutation.mutate({
      commentId,
      content: editContent.trim(),
    });
  };

  const handleDelete = (commentId: string) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      deleteCommentMutation.mutate(commentId);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getFileName = (url: string) => {
    try {
      const urlParts = url.split('/');
      return urlParts[urlParts.length - 1] || 'Unknown file';
    } catch {
      return 'Unknown file';
    }
  };

  const canEditDelete = (comment: TaskCommentWithUser) => {
    return (
      currentUser &&
      (comment.userId === currentUser.id ||
        currentUser.role === 'admin' ||
        currentUser.role === 'manager')
    );
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="flex items-center text-lg">
              <MessageCircle className="w-5 h-5 mr-2" />
              Comments ({comments.length})
            </CardTitle>
            {/* Toggle between Comment and Extension Request modes for non-admins */}
            {!isAdmin && task && (
              <div className="flex items-center gap-2 border rounded-md p-1 bg-gray-50 dark:bg-gray-800">
                <Button
                  variant={!isExtensionMode ? 'default' : 'ghost'}
                  size="sm"
                  className={`text-xs h-7 px-3 ${
                    !isExtensionMode
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                  onClick={() => setIsExtensionMode(false)}
                >
                  <MessageCircle className="w-3 h-3 mr-1" />
                  Comment
                </Button>
                <Button
                  variant={isExtensionMode ? 'default' : 'ghost'}
                  size="sm"
                  className={`text-xs h-7 px-3 ${
                    isExtensionMode
                      ? 'bg-orange-600 hover:bg-orange-700'
                      : 'hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                  onClick={() => setIsExtensionMode(true)}
                >
                  <CalendarPlus className="w-3 h-3 mr-1" />
                  Request Extension
                </Button>
              </div>
            )}
          </div>
          {canExtendDueDate && task && (
            <Dialog
              open={isExtendDueDateOpen}
              onOpenChange={setIsExtendDueDateOpen}
            >
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                >
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Extend Due Date
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Extend Task Due Date</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Current Due Date
                    </label>
                    <p className="text-sm text-gray-600">
                      {task.dueDate
                        ? new Date(task.dueDate).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })
                        : 'Not set'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      New Due Date
                    </label>
                    <Input
                      type="date"
                      value={newDueDate}
                      onChange={e => setNewDueDate(e.target.value)}
                      min={
                        task.dueDate
                          ? new Date(
                              new Date(task.dueDate).getTime() +
                                24 * 60 * 60 * 1000
                            )
                              .toISOString()
                              .split('T')[0]
                          : new Date().toISOString().split('T')[0]
                      }
                      className="w-full"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsExtendDueDateOpen(false);
                        setNewDueDate('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleExtendDueDate}
                      disabled={extendDueDateMutation.isPending || !newDueDate}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      {extendDueDateMutation.isPending
                        ? 'Extending...'
                        : 'Extend Due Date'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Comments List */}
        <div
          className="space-y-4 max-h-96 overflow-y-auto"
          data-testid="comments-list"
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading comments...</span>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No comments yet. Start the conversation!</p>
            </div>
          ) : (
            comments.map(comment => (
              <div
                key={comment.id}
                className="flex space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
              >
                <Avatar className="w-8 h-8 mt-1">
                  <AvatarImage
                    src={comment.user?.profileImageUrl || undefined}
                    alt={
                      comment.user
                        ? `${comment.user.firstName} ${comment.user.lastName}`
                        : 'User'
                    }
                  />
                  <AvatarFallback className="text-xs bg-gray-100 dark:bg-gray-700">
                    {comment.user?.firstName?.[0]}
                    {comment.user?.lastName?.[0]}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                        {comment.user
                          ? `${comment.user.firstName} ${comment.user.lastName}`
                          : 'Unknown User'}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {comment.user?.role?.replace('_', ' ')}
                      </Badge>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(comment.createdAt!).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>

                    {canEditDelete(comment) && (
                      <div className="flex items-center space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                          onClick={() => handleEdit(comment)}
                          data-testid={`button-edit-comment-${comment.id}`}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                          onClick={() => handleDelete(comment.id)}
                          data-testid={`button-delete-comment-${comment.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {editingComment === comment.id ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editContent}
                        onChange={e => setEditContent(e.target.value)}
                        placeholder="Edit your comment..."
                        className="min-h-[60px] text-sm"
                        data-testid="textarea-edit-comment"
                      />
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          onClick={() => handleUpdate(comment.id)}
                          disabled={updateCommentMutation.isPending}
                          data-testid="button-save-edit"
                        >
                          Save
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingComment(null);
                            setEditContent('');
                          }}
                          data-testid="button-cancel-edit"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {comment.content}
                      </p>

                      {comment.attachments &&
                        comment.attachments.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {comment.attachments.map((attachment, index) => (
                              <div
                                key={index}
                                className="flex items-center space-x-2 text-xs bg-white dark:bg-gray-700 rounded p-2 border"
                              >
                                <File className="w-4 h-4 text-gray-500" />
                                <span className="flex-1 truncate">
                                  {getFileName(attachment)}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-4 w-4 p-0 text-blue-600 hover:text-blue-800"
                                  onClick={async () => {
                                    try {
                                      const { downloadURL } = (await apiRequest(
                                        'POST',
                                        '/api/objects/presign-get',
                                        { objectUrl: attachment }
                                      )) as any;
                                      window.open(downloadURL, '_blank');
                                    } catch (e) {
                                      toast({
                                        title: 'Error',
                                        description:
                                          'Failed to get download link',
                                        variant: 'destructive',
                                      });
                                    }
                                  }}
                                  data-testid={`button-download-attachment-${index}`}
                                >
                                  <Download className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                    </>
                  )}
                </div>
              </div>
            ))
          )}
          <div ref={commentsEndRef} />
        </div>

        {/* New Comment Form */}
        <div className="border-t pt-4">
          {isExtensionMode && !isAdmin && (
            <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg">
              <div className="flex items-center mb-2">
                <CalendarPlus className="w-4 h-4 mr-2 text-orange-600" />
                <span className="text-sm font-medium text-orange-700 dark:text-orange-300">
                  Request Time Extension
                </span>
              </div>
              <p className="text-xs text-orange-600 dark:text-orange-400 mb-3">
                Please specify the number of days you need and provide a reason for the extension.
              </p>
              <div className="flex items-center gap-3 mb-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Days:
                </label>
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={extensionDays}
                  onChange={e => setExtensionDays(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20"
                  data-testid="input-extension-days"
                />
              </div>
            </div>
          )}
          <div className="space-y-3">
            <Textarea
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder={
                isExtensionMode
                  ? 'Please provide a reason for requesting this extension...'
                  : 'Write a comment...'
              }
              className="min-h-[80px]"
              data-testid="textarea-new-comment"
            />

            {attachments.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Attachments ({attachments.length}):
                </p>
                <div className="space-y-1">
                  {attachments.map((attachment, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-800 rounded p-2"
                    >
                      <div className="flex items-center space-x-2">
                        <File className="w-4 h-4 text-gray-500" />
                        <span className="truncate">
                          {getFileName(attachment)}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 text-red-500 hover:text-red-700"
                        onClick={() => removeAttachment(index)}
                        data-testid={`button-remove-attachment-${index}`}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileUpload}
                  data-testid="input-file-upload"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="button-attach-file"
                >
                  <Paperclip className="w-4 h-4 mr-1" />
                  {uploading ? 'Uploading...' : 'Attach Files'}
                </Button>
              </div>

              <div className="flex items-center gap-2">
                {isExtensionMode && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsExtensionMode(false)}
                    disabled={
                      createExtensionRequestMutation.isPending ||
                      createCommentMutation.isPending
                    }
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={handleSubmit}
                  disabled={
                    isExtensionMode
                      ? createExtensionRequestMutation.isPending ||
                        !newComment.trim()
                      : createCommentMutation.isPending ||
                        (!newComment.trim() && attachments.length === 0)
                  }
                  data-testid="button-post-comment"
                  className={isExtensionMode ? 'bg-orange-600 hover:bg-orange-700' : ''}
                >
                  {isExtensionMode ? (
                    <>
                      <CalendarPlus className="w-4 h-4 mr-1" />
                      Submit Extension Request
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-1" />
                      Post Comment
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
